// Pulse state: the per-member row the sweep reads and writes, the saved run,
// and the timing every part of the refresh loop shares (ENG-3425, ENG-3426).
// Spec: docs/specs/pulse.md §7 (what starts a run, the sweep, what costs
// money) and §10 item 5 (the state row). Timing ruled by Mason, 2026-09-29
// and 2026-09-30.
import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { PulseBoard, PulseCard } from "./Pulse.Board";
import { PulseCandidate } from "./Pulse.Candidates";
import { PulseTicketRow } from "./Pulse.Facts";
import { PulseNowBand, PulsePersona } from "./Pulse.Personas";

/** How often bull asks the bot to sweep. */
export const PULSE_SWEEP_EVERY_MS = 30 * 60 * 1000;
/** A sweep starts no new member after this, so it ends well before the next one. */
export const PULSE_SWEEP_BUDGET_MS = 20 * 60 * 1000;
/**
 * After an AI call for a member, the next waits at least this long, whether
 * the call worked or not (Mason, 2026-09-30: sweeps and rewrites both every 30
 * minutes). It counts from the start of the sweep that made the call and is a
 * little shorter than PULSE_SWEEP_EVERY_MS, so the next sweep can always
 * rewrite a member whose facts changed, while an extra sweep in between can't
 * call the AI for them twice.
 */
export const PULSE_AI_COOLDOWN_MINUTES = 25;
/** At most one mark write per org per this long per bot copy; a trailing write catches what arrives inside it. */
export const PULSE_MARK_THROTTLE_MS = 60 * 1000;
/** A claim older than this can be taken over: its bot copy died mid-run. */
export const PULSE_CLAIM_TTL_MINUTES = 10;
/** Staleness only counts for members who opened Pulse this recently, so idle members cost nothing. */
export const PULSE_OPENED_RECENTLY_DAYS = 7;
/** The page records an open at most this often, so reading it stays cheap. */
export const PULSE_OPENED_WRITE_EVERY_MINUTES = 5;
/** Runs older than this are pruned, except the one a member's page points at. */
export const PULSE_RUN_RETENTION_DAYS = 90;
/** A Pulse AI request that takes longer is abandoned and recorded as a failure. */
export const PULSE_AI_TIMEOUT_MS = 2 * 60 * 1000;
/** The freshness limits a member may choose instead of their persona's, in hours (ENG-3222 decision 12). */
export const PULSE_FRESHNESS_HOUR_CHOICES = [1, 3, 12] as const;
export type PulseFreshnessHours = (typeof PULSE_FRESHNESS_HOUR_CHOICES)[number];

/** Why a run called the AI. */
export enum PulseRunTrigger {
  /** The member had no page yet. */
  FIRST = "first",
  /** A durable candidate or one of the member's tickets changed since the newest run. */
  CHANGED = "changed",
  /** The page was older than the persona's freshness limit. */
  STALE = "stale",
  /** A staff preview in AI Studio. Previews are never saved; the value appears only on their receipts. */
  PREVIEW = "preview",
  /** A member's Explain on one card — on receipts only. */
  EXPLAIN = "explain",
  /** The member pressed Refresh: the AI writes the page even if nothing changed (ENG-3221 decision 1). */
  REFRESH = "refresh",
  /** The member's emailed page was due and something had changed, so the AI wrote it first (ENG-3222 decision 10). */
  SCHEDULED = "scheduled",
  /** A member asked a question (ENG-3223) — on receipts only. */
  ASK = "ask",
  /** The weekly theme finder (ENG-3225): one call per org, for no one member — on receipts only. */
  THEMES = "themes"
}

/**
 * What the location picker narrowed the page to (ENG-3222 decision 5): one
 * location in the member's reach, or one of a Regional Manager's groups
 * (decision 6, ENG-3225).
 */
export interface PulsePageScope {
  kind: "location" | "group";
  id: number;
}

/**
 * The emailed page (ENG-3222 decisions 8-11): every day, or on Mondays for a
 * weekly persona, at `hour` in `zone`.
 */
export interface PulseEmailSettings {
  on: boolean;
  /** The hour it's sent, 0-23. */
  hour: number;
  /** Where the hour is read: worked out by the server when the setting is saved, and again at each send (decision 9). */
  zone: string;
}

/** Per-member Pulse settings (spec §10 item 5), written by the page and by the Pulse tab in account settings. */
export interface PulseMemberSettings {
  /** The Now band's density, when the member changed it from their persona's; never below the persona's floor. */
  nowBand?: PulseNowBand;
  /** The location the page is narrowed to; absent or null for all of the member's locations. */
  scope?: PulsePageScope | null;
  /** Absent or null until the member turns the emailed page on. */
  email?: PulseEmailSettings | null;
  /** How old the page may get before it's rewritten, when the member chose; their persona's limit when absent or null. */
  freshnessHours?: PulseFreshnessHours | null;
}

/** One Pulse member's state row. Hot writes stay off the member table. */
export interface PulseMemberStateInterface extends OrgBaseInterface {
  memberId: number;
  /** The member's Pulse persona, set by an admin (plan Decision 7, ENG-3425). Null: no Pulse page. Lives here, not on `member`: it is Pulse state. */
  persona: PulsePersona | null;
  /** The mark: the oldest change not yet handled. Orders the sweep; null when nothing is waiting. */
  dirtyAt: Date | null;
  /** The newest change. A run clears the mark only if no change arrived after the one it saw. */
  lastMarkedAt: Date | null;
  /** Set while a sweep runs this member. */
  claimedAt: Date | null;
  /** The AI cooldown: the member is not claimed before this. */
  holdUntil: Date | null;
  /** When the member last opened Pulse (written by the page, phase 3). */
  lastOpenedAt: Date | null;
  /** When the newest run was saved. */
  lastRunAt: Date | null;
  /** The member's page is this run. */
  newestRunId: number | null;
  settings: PulseMemberSettings;
  /**
   * The emailed page's claim (ENG-3222): the first day, in the member's
   * zone, of the last period it was sent or tried for. Set before sending,
   * and put back only when there was no page yet to send.
   */
  lastEmailedOn: string | null;
}

/**
 * What the AI was told about live work at the run (ENG-3221 decision 14):
 * counts, never single items, which end within seconds of a ring.
 */
export interface PulseLiveSummary {
  /** Conversations waiting for a person that nobody is being offered, in the member's locations. */
  waiting: number;
  /** The longest of those waits, in words that stay true as time passes ("over 10 minutes"); null when none. */
  longestWait: string | null;
  /** Offers that rang out unanswered for the member in the last 15 minutes. */
  missedOffers: number;
  /** Teammates online now, from the roster; null when it couldn't be read. */
  teammatesOnline: number | null;
}

/** The member's card choices a run saw; the next run compares them to tell that a card choice was made (rider 3). */
export interface PulseCardChoicesSeen {
  /** Card ids pinned. */
  pinned: string[];
  /** Card ids dismissed or snoozed, so left out of the AI's options and picks. */
  hidden: string[];
}

/** What a run looked at: the candidate pool, the member's tickets, live work's summary and their card choices. */
export interface PulseRunInputs {
  candidates: PulseCandidate[];
  /** The first tickets of the member's assigned lane, in Ticketing's order; null when they could not be loaded. */
  tickets: PulseTicketRow[] | null;
  /** Null when live work doesn't apply to the member. */
  live: PulseLiveSummary | null;
  choices: PulseCardChoicesSeen;
}

/** What changed since the previous run, in the data and on the board — answers "why did this card appear or leave". */
export interface PulseRunChange {
  /** Durable candidate ids that are new, gone, or whose headline moved past the signal's threshold. */
  entered: string[];
  left: string[];
  moved: string[];
  tickets: { joined: number[]; left: number[]; changed: number[] };
  personaChanged: boolean;
  /** The member pinned, unpinned, dismissed or snoozed a card since the previous run (rider 3). */
  cardChoices: boolean;
  /** Pinned cards whose number moved past their signal's threshold since the previous run. */
  pinsMoved: string[];
  /** The AI's cards that arrived and left; `removedCards` keeps the cards themselves, for Earlier cards. */
  cards: { added: string[]; removed: string[]; removedCards: PulseCard[] };
}

export interface PulseRunInterface extends OrgBaseInterface {
  memberId: number;
  persona: PulsePersona;
  trigger: PulseRunTrigger;
  inputs: PulseRunInputs;
  board: PulseBoard;
  /** Null on a member's first run. */
  change: PulseRunChange | null;
  model: string | null;
  durationMs: number;
}

/** The bot's answer to bull's sweep call: false when this bot copy was already sweeping. */
export interface PulseSweepStart {
  started: boolean;
}

/**
 * The bot's answer to a member's Refresh: false only when the member has no
 * page state yet. A member a run holds is refreshed once that run finishes.
 */
export interface PulseRefreshStart {
  started: boolean;
}

/** A Refresh pressed on a page this recent re-reads it instead of calling the AI, so one member can't call it more than every five minutes. */
export const PULSE_REFRESH_MIN_AGE_MINUTES = 5;

/**
 * When Refresh can write a new page again: five minutes after the page was
 * written, or after the AI was last asked (decision 1). An AI call starts the
 * cooldown, so its start counts too: a call that failed can't be pressed
 * again at once. Null when it already can.
 *
 * A sweep counts its cooldown from the sweep's start, not the member's call,
 * so after a sweep's call fails, Refresh may call the AI again sooner than
 * five minutes after that call. Accepted (Mason, 9 October): it costs one
 * call, only on a Refresh pressed right after a failed sweep call.
 */
export function pulseRefreshAvailableAt(row: Pick<PulseMemberStateInterface, "lastRunAt" | "holdUntil"> | null, now: Date): Date | null {
  if (!row) return null;
  const minuteMs = 60_000;
  const lastRunAt = row.lastRunAt !== null ? new Date(row.lastRunAt).getTime() : null;
  const lastCallAt = row.holdUntil ? new Date(row.holdUntil).getTime() - PULSE_AI_COOLDOWN_MINUTES * minuteMs : null;
  const latest = Math.max(lastRunAt ?? -Infinity, lastCallAt ?? -Infinity);
  const availableAt = latest + PULSE_REFRESH_MIN_AGE_MINUTES * minuteMs;
  return availableAt > now.getTime() ? new Date(availableAt) : null;
}

/** Written, or asked of the AI, under five minutes ago. The server's Refresh and the bot's, on a member a run held, both ask it. */
export function pulsePageIsRecent(row: Pick<PulseMemberStateInterface, "lastRunAt" | "holdUntil"> | null, now: Date): boolean {
  return pulseRefreshAvailableAt(row, now) !== null;
}

/** The `pulse:refresh` socket event, sent to the member's room when a new run is saved. */
export interface PulseRefreshPayload {
  runId: number;
}
